import React, { memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faSlidersH } from '@fortawesome/free-solid-svg-icons';

import AssetV2MaintenanceMobileFiltersModal from './AssetV2MaintenanceMobileFiltersModal';
import CompleteMaintenanceModal from './CompleteMaintenanceModal';
import GatewayInstanceMaintenanceTable from './GatewayInstanceMaintenanceTable';
import GatewayInstanceMobileMaintenanceCard from './GatewayInstanceMobileMaintenanceCard';
import UpdateMaintenanceServiceValuesModal from './UpdateMaintenanceServiceValuesModal';
import ViewMaintenanceHistoryModal from './ViewMaintenanceHistoryModal';
import LoadingCircle from '../LoadingCircle';
import { NoItemsFound } from '../NoDataIndication';
import PaginationFooter from '../PaginationFooter';
import { MAINTENANCE_NOTIFICATION_STATUSES } from '../../Constants/GatewayReminderInstances';
import { GTEMPLATE_REMINDER_TYPES } from '../../Constants/GtemplateReminder';
import {
  MAINTENANCE,
  MAINTENANCE_LOG_CREATE,
  MAINTENANCE_MANAGE,
} from '../../Constants/RBAC';
import { Width } from '../../Context/WidthContext';
import Can from '../../RBAC/Can';
import { STICKY_OVERRIDE } from '../../Styles/Z-INDEX';

export default memo(AssetV2MaintenanceTabDisplay);

function AssetV2MaintenanceTabDisplay(props) {
  const { assetID } = useParams(),
    showEmptyState = useMemo(
      () => !props.instances.length && !props.error && !props.reminder_name,
      [props.error, props.instances.length, props.reminder_name]
    ),
    showHistoryModal = useMemo(
      () => !!props.gateway_id || !!props.gateway_reminder_instance_id,
      [props.gateway_id, props.gateway_reminder_instance_id]
    ),
    width = useContext(Width);

  return (
    <>
      <button
        className="d-lg-none btn btn-link p-0 shadow-none text-decoration-none position-absolute"
        onClick={props.toggleMobileFilters}
        style={{ right: '0.5rem', top: '-5.7rem', zIndex: STICKY_OVERRIDE }}
      >
        <FontAwesomeIcon
          className="text-brand-primary"
          icon={faSlidersH}
          size="lg"
        />
      </button>
      <div className="position-absolute overflow-auto h-100 w-100 bg-white rounded-lg d-flex flex-column p-2 p-lg-3 px-lg-5">
        <div className="d-flex align-items-center justify-content-between justify-content-lg-start">
          <div className="d-flex flex-lg-column align-items-center align-items-lg-start">
            <h5 className="text-danger mb-0 mb-lg-3">
              {props.instancesLoading ? (
                <FontAwesomeIcon icon={faMinus} />
              ) : (
                props.due
              )}
            </h5>
            <p className="text-muted nine-tenth-text mb-0 mb-lg-3 ml-3 ml-lg-0">
              Reminder Overdue
            </p>
          </div>
          <div className="d-flex flex-lg-column ml-lg-4 align-items-center align-items-lg-start">
            <h5 className="text-warning mb-0 mb-lg-3">
              {props.instancesLoading ? (
                <FontAwesomeIcon icon={faMinus} />
              ) : (
                props.upcoming
              )}
            </h5>
            <p className="text-muted nine-tenth-text mb-0 mb-lg-3 ml-3 ml-lg-0">
              Reminder Upcoming
            </p>
          </div>
        </div>
        <div className="flex-fill d-flex flex-column">
          <div className="w-100 d-flex align-items-center justify-content-between justify-content-lg-start my-2 my-lg-3">
            <Can
              no={() => null}
              orgID={props.orgID}
              perform={MAINTENANCE_LOG_CREATE}
              resource={MAINTENANCE}
              yes={() => (
                <button
                  className="btn modal-button-primary three-quarter-text"
                  disabled={
                    props.instancesLoading || !props.checkedInstances?.size
                  }
                  onClick={props.toggleCompleteMaintenanceModal}
                >
                  Complete Maintenance
                </button>
              )}
            />
            <button
              className="btn btn-outline-secondary three-quarter-text ml-lg-5"
              disabled={props.instancesLoading}
              onClick={() => props.toggleHistoryModal({ gateway_id: assetID })}
            >
              View History
            </button>
            <Can
              no={() => null}
              orgID={props.orgID}
              perform={MAINTENANCE_MANAGE}
              resource={MAINTENANCE}
              yes={() => (
                <button
                  className="btn modal-button-primary three-quarter-text ml-auto"
                  disabled={
                    props.instancesLoading || !props.checkedInstances?.size
                  }
                  onClick={props.toggleUpdateServiceValuesModal}
                >
                  Update Service Values
                </button>
              )}
            />
          </div>
          {props.error ? (
            <p className="w-100 mb-0 nine-tenth-text text-danger">
              {props.error}
            </p>
          ) : null}
          <div className="flex-fill mt-2 position-relative">
            {(() => {
              switch (true) {
                case props.instancesLoading:
                  return <LoadingCircle size="4x" />;
                case !props.instances.length && props.error:
                  return (
                    <NoItemsFound text="There was an error fetching your data. Please contact an administrator if the problem persists." />
                  );
                case showEmptyState:
                  return <NoItemsFound text="No reminders for this asset." />;
                default:
                  return width > 991 ? (
                    <div className="position-absolute h-100 w-100 overflow-auto">
                      <GatewayInstanceMaintenanceTable {...props} />
                    </div>
                  ) : (
                    props.instances?.map((instance, i) => (
                      <div
                        className="d-lg-none m-2"
                        key={`${instance._id}-${i}`}
                      >
                        <GatewayInstanceMobileMaintenanceCard
                          {...props}
                          instance={instance}
                        />
                      </div>
                    ))
                  );
              }
            })()}
          </div>
        </div>
        <PaginationFooter
          handlePageChange={props.handlePageChange}
          page={props.page}
          pages={props.pages}
          rowsLength={props.instances?.length ?? 0}
          rowsLoading={props.instancesLoading}
        />
        {showHistoryModal ? (
          <ViewMaintenanceHistoryModal
            assetNickname={props.assetNickname}
            gateway_id={props.gateway_id}
            gateway_reminder_instance_id={props.gateway_reminder_instance_id}
            isOpen={showHistoryModal}
            signalUnitMap={props.signalUnitMap}
            toggleModal={props.toggleHistoryModal}
          />
        ) : null}
        {props.showCompleteMaintenanceModal ? (
          <CompleteMaintenanceModal
            checkedInstances={props.checkedInstances}
            handleCompleteMaintenanceSuccess={
              props.handleCompleteMaintenanceSuccess
            }
            instanceShadowValueMap={props.instanceShadowValueMap}
            isOpen={props.showCompleteMaintenanceModal}
            signalUnitMap={props.signalUnitMap}
            toggleModal={props.toggleCompleteMaintenanceModal}
          />
        ) : null}
        {props.showUpdateServiceValuesModal ? (
          <UpdateMaintenanceServiceValuesModal
            checkedInstances={props.checkedInstances}
            handleUpdateServiceValuesSuccess={
              props.handleUpdateServiceValuesSuccess
            }
            instanceShadowValueMap={props.instanceShadowValueMap}
            isOpen={props.showUpdateServiceValuesModal}
            signalUnitMap={props.signalUnitMap}
            toggleModal={props.toggleUpdateServiceValuesModal}
          />
        ) : null}
        <AssetV2MaintenanceMobileFiltersModal
          {...props}
          isOpen={props.showMobileFilters}
          toggleModal={() => props.toggleMobileFilters()}
        />
      </div>
    </>
  );
}

AssetV2MaintenanceTabDisplay.propTypes = {
  assetNickname: PropTypes.string,
  checkedInstances: PropTypes.object,
  count: PropTypes.number,
  due: PropTypes.number,
  error: PropTypes.string,
  gateway_id: PropTypes.string,
  gateway_reminder_instance_id: PropTypes.string,
  handleClearFiltersClick: PropTypes.func,
  handleCompleteMaintenanceSuccess: PropTypes.func,
  handlePageChange: PropTypes.func,
  handleStatePropUpdate: PropTypes.func,
  handleUpdateServiceValuesSuccess: PropTypes.func,
  instances: PropTypes.arrayOf(
    PropTypes.shape({
      GtemplateReminder: PropTypes.shape({
        attachment: PropTypes.string,
        message: PropTypes.string,
        name: PropTypes.string,
        trigger_conditions: PropTypes.shape({
          calendar_trigger: PropTypes.shape({
            interval: PropTypes.string,
            margin: PropTypes.string,
          }),
          signal_trigger: PropTypes.shape({
            interval: PropTypes.number,
            margin: PropTypes.number,
          }),
        }),
        type: PropTypes.oneOf(GTEMPLATE_REMINDER_TYPES),
      }),
      MaintenanceNotifications: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          maintenance_completed: PropTypes.bool,
          signal_value: PropTypes.string,
          status: PropTypes.oneOf(MAINTENANCE_NOTIFICATION_STATUSES),
          MaintenanceHistoryLog: PropTypes.shape({
            contact_info: PropTypes.string,
            message: PropTypes.string,
            service_date: PropTypes.string,
            signal_value_when_serviced: PropTypes.string,
            user_id: PropTypes.string,
          }),
        })
      ),
      next_signal_value_due: PropTypes.string,
      next_time_due: PropTypes.string,
    })
  ),
  instanceShadowValueMap: PropTypes.object,
  instancesLoading: PropTypes.bool,
  orgID: PropTypes.string,
  page: PropTypes.number,
  pages: PropTypes.number,
  reminder_name: PropTypes.string,
  showCompleteMaintenanceModal: PropTypes.bool,
  showHistoryModal: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  showMobileFilters: PropTypes.bool,
  showUpdateServiceValuesModal: PropTypes.bool,
  signalUnitMap: PropTypes.object,
  toggleCompleteMaintenanceModal: PropTypes.func,
  toggleHistoryModal: PropTypes.func,
  toggleMobileFilters: PropTypes.func,
  toggleUpdateServiceValuesModal: PropTypes.func,
  upcoming: PropTypes.number,
};
